/**
 * NotificationToast Component
 *
 * Custom Sonner toast for money notifications (trades, swaps, deposits,
 * withdrawals, fees). Replaces the bare `toast(notification.message)` call
 * in useNotifications which produced an unstyled toast with no close button
 * and a raw Helius tx.description string containing untruncated wallets.
 *
 * Design system:
 *   - Surface #111111 with #222222 border
 *   - Brand accent #CCFF00 left border for buys / fees
 *   - #F87171 left border for sells / withdrawals
 *   - Truncates long base58 addresses anywhere in the message
 *   - Always renders an X close affordance in the top-right
 */

import { ArrowLeftRight, ArrowUpRight, ArrowDownLeft, Coins, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppNotification, NotificationType } from '@/lib/notifications';

interface NotificationToastProps {
  notification: AppNotification;
  onDismiss: () => void;
}

// Truncate any base58 Solana address (32-44 chars) found inside a string.
// Helius tx.description embeds full wallets — we strip those down so the
// toast stays scannable.
const BASE58_RE = /\b[1-9A-HJ-NP-Za-km-z]{32,44}\b/g;
function truncateAddressesInText(text: string): string {
  return text.replace(BASE58_RE, (addr) => `${addr.slice(0, 4)}...${addr.slice(-4)}`);
}

interface TypeStyle {
  Icon: typeof ArrowLeftRight;
  accentClass: string;
  iconClass: string;
}

function getTypeStyle(type: NotificationType): TypeStyle {
  switch (type) {
    case 'trade_executed':
    case 'swap_completed':
      return {
        Icon: ArrowLeftRight,
        accentClass: 'border-l-[#CCFF00]',
        iconClass: 'text-[#CCFF00]',
      };
    case 'deposit_received':
      return {
        Icon: ArrowDownLeft,
        accentClass: 'border-l-[#4ADE80]',
        iconClass: 'text-[#4ADE80]',
      };
    case 'withdrawal_completed':
      return {
        Icon: ArrowUpRight,
        accentClass: 'border-l-[#F87171]',
        iconClass: 'text-[#F87171]',
      };
    case 'creator_fees_claimable':
    case 'creator_fees_claimed':
    case 'fee_share_received':
      return {
        Icon: Coins,
        accentClass: 'border-l-[#CCFF00]',
        iconClass: 'text-[#CCFF00]',
      };
    default:
      return {
        Icon: ArrowLeftRight,
        accentClass: 'border-l-[#CCFF00]',
        iconClass: 'text-[#CCFF00]',
      };
  }
}

export function NotificationToast({ notification, onDismiss }: NotificationToastProps) {
  const { Icon, accentClass, iconClass } = getTypeStyle(notification.type);
  const message = truncateAddressesInText(notification.message);

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-xl border border-[#222222] border-l-4 shadow-xl',
        'bg-[#111111]',
        'animate-in slide-in-from-right-full duration-300',
        accentClass
      )}
      style={{ width: '340px', maxWidth: 'calc(100vw - 32px)' }}
    >
      <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', iconClass)} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">
          {notification.title}
        </div>
        <div className="text-xs text-[#888888] mt-0.5 break-words">
          {message}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-[#666666] hover:text-white transition-colors p-1 -mt-1 -mr-1 flex-shrink-0 min-w-[24px] min-h-[24px] flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default NotificationToast;
